"use client";

import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { Fragment } from "react";
import Button from "@/app/(components)/generics/Button/Button";
import ChangeLanguage from "@/app/(components)/generics/ChangeLanguage/ChangeLanguage";
import IconMenu from "@/app/(components)/generics/Icons/IconMenu";
import Logo from "@/app/(components)/generics/Logo/Logo";
import { useAuthUser } from "@/app/(components)/specifics/wrappers/AuthProvider/AuthProvider";
import { ModalStore } from "@/app/stores/ModalStore";
import { SidebarStore, useSidebarOpen } from "@/app/stores/SidebarStore";
import { TKey } from "@/app/translations/TKey";
import { useT } from "@/app/translations/useT";
import { DefaultProps } from "@/app/types/common/DefaultProps";
import { UStyles } from "@/app/utils/UStyles";
import { PublicPaths } from "@/app/vars/Routes";
import styles from "./Header.module.css";

const HOME_SECTIONS = [
  { id: "why", labelKey: TKey.HOME_MEIDLY_WHY_SUP_TITLE },
  { id: "who", labelKey: TKey.HOME_TESTIMONIALS_SUP_TITLE },
  { id: "tarifs", labelKey: TKey.HOME_MEIDLY_PRICING_SUP_TITLE },
  { id: "faq", labelKey: TKey.FAQ },
];

type Props = DefaultProps & {
  initialSidebarOpen: boolean;
};

export default function Header(props: Props) {
  const T = useT();
  const { params } = props;
  const { lang } = params;

  const user = useAuthUser();
  const isOpen = useSidebarOpen(props.initialSidebarOpen);
  const toggleSidebar = SidebarStore((state) => state.toggleSidebar);
  const pathname = usePathname();
  const router = useRouter();

  const isHomePage = pathname === `/${lang}` || pathname === `/${lang}/`;
  const isBlogPage =
    pathname === `/${lang}${PublicPaths.BLOG}` ||
    pathname.startsWith(`/${lang}${PublicPaths.BLOG}/`);

  const scrollToSection = (id: string) => {
    const target = document.getElementById(id);
    if (target) {
      target.scrollIntoView({ behavior: "smooth" });
    } else {
      router.push(`/${lang}#${id}`);
    }
  };

  if (user) {
    return (
      <header className={UStyles.classNames(styles.container, styles.loggedMobile)}>
        <div className={styles.content}>
          <div className={styles.leftPart}>
            <button
              className={styles.toggleBtn}
              aria-label={T(TKey.SIDEBAR_TOGGLE)}
              onClick={toggleSidebar}
            >
              <IconMenu />
            </button>
            <Logo className={styles.mobileLogo} />
          </div>
        </div>
      </header>
    );
  }

  return (
    <header
      className={UStyles.classNames(
        styles.container,
        isOpen ? styles.sidebarOpen : styles.sidebarClosed,
      )}
    >
      <div className={styles.content}>
        <div className={styles.leftPart}>
          <button
            className={styles.toggleBtn}
            aria-label={T(TKey.SIDEBAR_TOGGLE)}
            onClick={toggleSidebar}
          >
            <IconMenu />
          </button>

          <Logo className={styles.mobileLogo} />

          <p className={styles.welcomeText}>
            <span className={styles.wText}>{isHomePage && <>{T(TKey.WELCOME_TO_MEIDLY)} </>}</span>
            <span className={styles.wave}>{T(TKey.WELCOME_EMOJI)}</span>
          </p>

          <nav className={styles.homeNav}>
            {isHomePage && (
              <>
                <span className={styles.homeNavSeparator}>|</span>
                {HOME_SECTIONS.map((section, index) => (
                  <Fragment key={section.id}>
                    {index > 0 && <span className={styles.homeNavDot}>·</span>}
                    <button
                      type="button"
                      className={styles.homeNavLink}
                      onClick={() => scrollToSection(section.id)}
                    >
                      {T(section.labelKey)}
                    </button>
                  </Fragment>
                ))}
              </>
            )}

            <span className={styles.homeNavSeparator}>|</span>
            <Link
              href={`/${lang}${PublicPaths.BLOG}`}
              prefetch={true}
              className={UStyles.classNames(
                styles.homeNavLink,
                isBlogPage ? styles.homeNavLinkActive : "",
              )}
            >
              {T(TKey.BLOG_NAV_LABEL)}
            </Link>
          </nav>
        </div>

        <div className={styles.centerPart}></div>

        <div className={styles.rightPart}>
          <div className={styles.menuLogin}>
            <ChangeLanguage params={params} />
            <Button
              params={params}
              text={T(TKey.LOG_IN)}
              isLink={true}
              href="#"
              isResponsiveLink={true}
              onClick={(e: any) => {
                e.preventDefault();
                ModalStore.getState().openSignIn(props.params);
              }}
              min={true}
            />

            <Button
              params={params}
              text={T(TKey.HOME_CTA_START_FREE)}
              isLink={false}
              onClick={() => {
                ModalStore.getState().openSignUp(props.params);
              }}
              min={true}
              className={styles.mobileHidden}
            />
          </div>
        </div>
      </div>
    </header>
  );
}
